import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LuScanFace } from 'react-icons/lu';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import PageHeader from '../components/oracle/PageHeader';
import LogoSpinner from '../components/oracle/LogoSpinner';
import EmptyState from '../components/oracle/EmptyState';
import SegmentedSwitch from '../components/oracle/SegmentedSwitch';
import ScanJudgeCard from '../components/judge/ScanJudgeCard';
import DeckTransition from '../components/oracle/DeckTransition';
import UnlockProgress from '../components/oracle/UnlockProgress';
import { COPY } from '../contracts/copyMap';
import { ROUTES, vaultEntryPath } from '../contracts/routes';
import { UNLOCK_FEEDBACK_QUOTA } from '../contracts/scanFeedbackApi';
import { useUser } from '../contexts/UserContext';
import useEntitlement from '../hooks/useEntitlement';
import { hasOutstandingUnlock } from '../lib/scanQuota';

// EXPLORE — /judge. The scan-judging deck.
//
// The scan deck is the primary surface here; the two-photo Glowup deck moves
// behind a segmented switch (?mode=glowup), the same pattern Create.js already
// uses for scan/glowup. That is deliberate: BottomTabBar caps at 4 tabs and is
// full, so a new top-level surface would have to evict an existing one.
//
//   ┌───────────────────────────────────────────────┐
//   │ EXPLORE               [ SCANS │ GLOWUPS ]     │
//   ├───────────────────────────────────────────────┤
//   │ ┌─────────┐  STRONGEST FEATURES   [chips]     │
//   │ │  scan   │  AREAS TO WORK ON     [chips]     │
//   │ │ ▬▬bar▬▬ │  SUBMIT JUDGMENT / SKIP           │
//   │ └─────────┘                                    │
//   └───────────────────────────────────────────────┘
//
// Deck state machine:
//
//   loading ──▶ ready ──submit/skip──▶ ready (cursor+1)
//      │           │
//      │           └──cursor past end, next_cursor──▶ loading more
//      ├──▶ empty  (DECK_EMPTY — nothing feedback_enabled exists yet)
//      └──▶ error
//
// A submit that fails does NOT advance. Advancing would silently drop the
// reading and quietly cost the judge one of the ten they are working toward.

const PAGE_SIZE = 20;
// Refill this many cards from the end so the judge never waits mid-session.
const PREFETCH_AT_REMAINING = 3;

// Hoisted to module scope on purpose. Defined inside the page body it would be
// a NEW component type on every render, so React would unmount and remount the
// whole subtree each time — blowing away the in-progress chip selection.
function ModeSwitch({ size, stretch, onGlowup }) {
  return (
    <SegmentedSwitch
      options={[
        { key: 'scan', label: COPY.SCAN_JUDGE_TAB },
        { key: 'glowup', label: COPY.GLOWUP_JUDGE_TAB },
      ]}
      active="scan"
      size={size}
      stretch={stretch}
      onChange={(key) => { if (key === 'glowup') onGlowup(); }}
    />
  );
}

// `pending` is the scan id this progress counts toward, or null. It comes from
// the server rather than from the ?unlock= param, which is why the row now
// appears for someone who reached Explore from the bottom tab bar — previously
// the whole indicator was conditional on that param, so the only people who
// ever saw their progress were the ones who arrived from the unlock modal.
//
// It cannot be inferred from `progress`: "0 of 10" means both "sealed, nothing
// judged yet" and "nothing sealed at all".
function Shell({ children, pending, progress, onGlowup }) {
  return (
    <AppLayout
      header={(
        <PageHeader
          title={COPY.SCAN_JUDGE_EYEBROW}
          subtitle={COPY.SCAN_JUDGE_HEADLINE}
          desktopRight={(
            <div className="hidden lg:flex items-center mt-[18px]">
              <ModeSwitch onGlowup={onGlowup} />
            </div>
          )}
        />
      )}
    >
      <div className="px-5 lg:px-12 pb-10 lg:pb-14">
        {/* Mobile switch — the header slot is desktop-only. */}
        <div className="lg:hidden mb-5">
          <ModeSwitch size="sm" stretch onGlowup={onGlowup} />
        </div>

        {pending && (
          <div className="mb-5" data-testid="judge-progress">
            <p className="m-0 mb-2 font-display uppercase text-[11px] tracking-[0.18em] text-aurum">
              {COPY.SCAN_JUDGE_PROGRESS_PREFIX}
            </p>
            {/* Same component as the unlock modal, so the two can never
                disagree about the count — but the continuous form here. Ten
                cells directly above the judging card's own ten-cell
                ScorePicker put two rows of ten boxes on one screen, one
                reporting and one collecting. */}
            <UnlockProgress
              done={progress.done}
              required={progress.required}
              full
              variant="bar"
              label={COPY.SCAN_JUDGE_PROGRESS_PREFIX}
            />
          </div>
        )}

        {children}
      </div>
    </AppLayout>
  );
}

export default function ScanJudge() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read ONLY to decide whether an empty deck should offer the membership
  // escape hatch — see the empty branch. Explore itself is open to everyone
  // and nothing here gates on entitlement.
  const { userData } = useUser() || {};
  const { isPro, entitlement } = useEntitlement();

  const [state, setState] = useState({ phase: 'loading', deck: [], nextCursor: null });
  const [cursor, setCursor] = useState(0);
  const [strengths, setStrengths] = useState([]);
  const [improvements, setImprovements] = useState([]);
  // Required before SUBMIT enables. null, not 0 — 0 is outside the 1-10 range
  // and would read as a real rating to anything that coerces it.
  const [judgeScore, setJudgeScore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ done: 0, required: UNLOCK_FEEDBACK_QUOTA });

  // Which of the caller's own scans this progress counts toward, or null.
  //
  // Seeded from the ?unlock= param so the row is on screen for the very first
  // paint when the judge arrives from the unlock modal, then replaced by the
  // server's answer once the deck loads. The param is a hint about where they
  // came from; the server is the authority on what is actually sealed, which
  // matters for anyone who reaches Explore from the bottom tab bar and has no
  // param at all — they used to see no progress whatsoever.
  const [pendingScanId, setPendingScanId] = useState(() => searchParams.get('unlock'));
  const goGlowup = useCallback(
    () => setSearchParams({ mode: 'glowup' }, { replace: true }),
    [setSearchParams],
  );

  const loadDeck = useCallback(async (cursorToken = null) => {
    if (!cursorToken) setState((s) => ({ ...s, phase: 'loading' }));
    try {
      const getDeck = httpsCallable(functions, 'getScanFeedbackDeck');
      const res = await getDeck({ limit: PAGE_SIZE, ...(cursorToken ? { cursor: cursorToken } : {}) });
      const data = res?.data;

      if (data?.progress) setProgress(data.progress);
      // `pending_scan_id` is authoritative including when it is null — that is
      // how the row disappears once the sealed scan opens. Guarded on the key
      // being present so a pre-deploy response shape cannot blank it.
      if (data && 'pending_scan_id' in data) setPendingScanId(data.pending_scan_id);

      if (data?.status !== 'success') {
        // DECK_EMPTY is a state, not a failure — nothing is feedback_enabled yet.
        setState((s) => ({
          ...s,
          phase: data?.error_code === 'DECK_EMPTY' ? 'empty' : 'error',
        }));
        return;
      }

      setState((s) => ({
        phase: 'ready',
        deck: cursorToken ? [...s.deck, ...data.deck] : data.deck,
        nextCursor: data.next_cursor || null,
      }));
    } catch (err) {
      console.error('ScanJudge: deck load failed', err);
      setState((s) => ({ ...s, phase: 'error' }));
    }
  }, []);

  useEffect(() => { loadDeck(); }, [loadDeck]);

  // Top up before the judge reaches the end, so a continuous session never
  // stalls on a round trip.
  useEffect(() => {
    if (state.phase !== 'ready' || !state.nextCursor) return;
    if (state.deck.length - cursor > PREFETCH_AT_REMAINING) return;
    loadDeck(state.nextCursor);
  }, [state.phase, state.deck.length, state.nextCursor, cursor, loadDeck]);

  const resetSelection = () => {
    setStrengths([]);
    setImprovements([]);
    setJudgeScore(null);
    setError(null);
  };

  const advance = () => {
    setCursor((c) => c + 1);
    resetSelection();
  };

  const toggle = (list, setList) => (key) => {
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  };

  const card = state.deck[cursor];

  const submit = async () => {
    if (!card || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const submitJudgment = httpsCallable(functions, 'submitScanFeedback');
      const res = await submitJudgment({
        scan_id: card.scan_id,
        card_token: card.card_token,
        issued_at: card.issued_at,
        strengths,
        improvements,
        rating: judgeScore,
        // Telemetry only — the server measures dwell from its own minted
        // issued_at, so this can never buy a faster submission.
        dwell_ms: Date.now() - card.issued_at,
      });
      const data = res?.data;

      if (data?.status !== 'success') {
        const messages = {
          TOO_FAST: COPY.SCAN_JUDGE_TOO_FAST,
          LOW_EFFORT: COPY.SCAN_JUDGE_LOW_EFFORT,
          RATE_LIMITED: COPY.SCAN_JUDGE_RATE_LIMITED,
        };
        // A duplicate or withdrawn portrait is not the judge's problem —
        // move them on rather than stranding them on a dead card.
        if (data?.error_code === 'DUPLICATE_FEEDBACK' || data?.error_code === 'FEEDBACK_DISABLED') {
          advance();
          return;
        }
        // LOW_EFFORT means the deck should be refreshed, not retried in place.
        if (data?.error_code === 'LOW_EFFORT') {
          setError(COPY.SCAN_JUDGE_LOW_EFFORT);
          return;
        }
        setError(messages[data?.error_code] || COPY.SCAN_JUDGE_ERROR);
        return;
      }

      if (data.progress) setProgress(data.progress);

      // The payoff. The server decides what unlocked; we just go there.
      if (data.unlocked_scan_id) {
        navigate(`${vaultEntryPath(data.unlocked_scan_id)}?reveal=1`);
        return;
      }
      advance();
    } catch (err) {
      console.error('ScanJudge: submit failed', err);
      setError(COPY.SCAN_JUDGE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  if (state.phase === 'loading') {
    return (
      <Shell pending={pendingScanId} progress={progress} onGlowup={goGlowup}>
        <div className="min-h-[45vh] flex items-center justify-center"><LogoSpinner /></div>
      </Shell>
    );
  }

  if (state.phase === 'error') {
    return (
      <Shell pending={pendingScanId} progress={progress} onGlowup={goGlowup}>
        <EmptyState
          headline="EXPLORE IS UNAVAILABLE"
          subtitle={COPY.SCAN_JUDGE_ERROR}
          ctaLabel={COPY.RETRY_CTA}
          onCta={() => loadDeck()}
          icon={<LuScanFace size={22} />}
        />
      </Shell>
    );
  }

  // Ran past the loaded deck with another page inbound — hold rather than
  // flashing the empty state.
  if (!card && state.nextCursor) {
    return (
      <Shell pending={pendingScanId} progress={progress} onGlowup={goGlowup}>
        <div className="min-h-[45vh] flex items-center justify-center"><LogoSpinner /></div>
      </Shell>
    );
  }

  if (state.phase === 'empty' || !card) {
    // ── THE MEMBERSHIP PATH IS AN ESCAPE HATCH, NOT A GATE ──────────────────
    //
    // Explore is open to everyone and always was, so an empty deck is not a
    // paywall — it is just an empty deck. This branch used to show BEGIN FREE
    // TRIAL unconditionally, which read as though membership were required to
    // browse, and showed a trial offer to people who were ALREADY TRIALING.
    //
    // `hasOutstandingUnlock` answers the only question that matters here —
    // "is there something membership would actually do for this person right
    // now?" — and answers it correctly in all three cases, because it returns
    // false for a member before it looks at anything else:
    //
    //   member / trialing        -> nothing to sell; they have it
    //   free, nothing sealed     -> nothing to unlock; they are just browsing
    //   free, a sealed reading   -> the deck is empty, so feedback cannot open
    //                               it today. THIS is when the offer helps.
    //
    // Even then it stays the SECONDARY action behind RETRY: the deck refilling
    // is the outcome the user came for.
    const canUnlockWithMembership = hasOutstandingUnlock(userData, { isPro });

    return (
      <Shell pending={pendingScanId} progress={progress} onGlowup={goGlowup}>
        <EmptyState
          headline={COPY.SCAN_JUDGE_EMPTY_HEADLINE}
          subtitle={canUnlockWithMembership
            ? COPY.SCAN_JUDGE_EMPTY_BODY
            : COPY.SCAN_JUDGE_EMPTY_BODY_OPEN}
          ctaLabel={COPY.RETRY_CTA}
          onCta={() => { setCursor(0); loadDeck(); }}
          // "BEGIN FREE TRIAL" is a promise Stripe will not keep for someone
          // who has already spent theirs.
          secondaryCtaLabel={canUnlockWithMembership
            ? (entitlement?.has_used_trial ? COPY.MEMBERSHIP_CTA_NO_TRIAL : COPY.MEMBERSHIP_CTA)
            : undefined}
          onSecondaryCta={() => navigate(ROUTES.MEMBERSHIP)}
          icon={<LuScanFace size={22} />}
        />
      </Shell>
    );
  }

  return (
    <Shell pending={pendingScanId} progress={progress} onGlowup={goGlowup}>
      {/* The deck only ever moves forward here — there is no going back to a
          portrait you already judged — so the direction is fixed at 1. */}
      <DeckTransition cardKey={card.scan_id}>
        <ScanJudgeCard
          card={card}
          strengths={strengths}
          improvements={improvements}
          onToggleStrength={toggle(strengths, setStrengths)}
          onToggleImprovement={toggle(improvements, setImprovements)}
          judgeScore={judgeScore}
          onScoreChange={setJudgeScore}
          onSubmit={submit}
          onSkip={advance}
          submitting={submitting}
          error={error}
        />
      </DeckTransition>
    </Shell>
  );
}
